import { Add } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLoaderData, useMatch, useNavigate } from 'react-router';
import { useTypedHash, useTypedSearchParams, useTypedState } from 'react-router-typesafe-routes';
import { Virtuoso } from 'react-virtuoso';
import DateDisplay from '@/components/common/date-display';
import Modal from '@/components/common/modal/modal';
import TabList from '@/components/common/tablist';
import EmptyLayout from '@/components/layouts/empty-layout';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import CollectionItem from '@/components/user/collection-item/collection-item';
import CollectionMenu from '@/components/user/collection-item/collection-menu';
import type { Collection } from '@/hooks/collections/use-collections';
import useDeleteCollection from '@/hooks/collections/use-delete-collection';
import useFilteredCollections, {
  type CollectionFilterReducerFilterType,
} from '@/hooks/collections/use-filtered-collections';
import useSendCollection from '@/hooks/collections/use-send-collection';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useScrollRestoration from '@/hooks/use-scroll-restoration';
import useUserPlatform, { useIsDesktop } from '@/hooks/use-user-platform';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES, { type PromiseLoaderReturnType } from '@/routes';
import { useEmbedLayout } from '@/stores/device';
import { makePrompt } from '@/stores/prompter';
import { BUILD_EMBED_TYPE } from '@/utils/constants';
import copyTextToClipboard from '@/utils/copy-text';
import { EmbedLayout } from '@/utils/embed-layout';

interface Props {
  type?: ConversationType;
  id?: number;
}

export function CollectionsIndexRoute({ type: sessionType, id: sessionId }: Props) {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hash = useTypedHash(ROUTES.MY.COLLECTIONS);
  const { groupedCollections, filterType, setFilterType } = useFilteredCollections(undefined, 'backward', hash);
  const { mutateAsync: deleteCollection } = useDeleteCollection();
  const { dispatch } = useCommDispatcher();
  const layout = useEmbedLayout();
  const [searchParams] = useTypedSearchParams(ROUTES.MY.COLLECTIONS);

  const isDesktop = useIsDesktop();
  const isDesktopModal = Boolean(isDesktop && sessionType);

  const { sendCollection } = useSendCollection(sessionType, sessionId);

  useScrollRestoration();

  const handleChangeFilter = (type: CollectionFilterReducerFilterType) => {
    setFilterType(type);
    if (sessionId) {
      const route =
        sessionType === 'session' ? ROUTES.MESSAGES.SESSIONS.COLLECTIONS : ROUTES.MESSAGES.GROUPS.COLLECTIONS;
      const url = route.$buildPath({
        params: { id: sessionId },
        hash: type === 'all' ? undefined : type,
      });
      if (BUILD_EMBED_TYPE !== 'embed-native') {
        history.replaceState({ ...history.state }, '', url);
      } else {
        navigate(url, { replace: true });
      }
    } else {
      const url = ROUTES.MY.COLLECTIONS.$buildPath({ searchParams, hash: type === 'all' ? undefined : type });
      navigate(url, { replace: true });
    }
  };

  const handleForward = async (collection: Collection) => {
    if (sessionType && sessionId) {
      await sendCollection(collection);
    } else {
      navigate(ROUTES.MY.COLLECTIONS.BY_ID.FORWARD.$buildPath({ params: { collectionId: collection.ID } }), {
        state: { skipPreview: 1 },
      });
    }
  };

  const handleSummonContextMenu = (collection: Collection, position?: [number, number] | null) => {
    makePrompt({
      key: 'collection-context-menu',
      type: 'context-menu',
      content: ({ resolve }) => (
        <CollectionMenu
          collection={collection}
          contextMenuType={sessionType ? 'forward' : 'normal'}
          onResolve={(resolution) => resolve(resolution)}
        />
      ),
      meta: {
        position,
      },
      promise: async (resolution) => {
        if (typeof resolution !== 'string') return;

        switch (resolution) {
          case 'forward':
            await handleForward(collection);
            break;
          case 'delete':
            makePrompt({
              key: 'delete-collection',
              type: 'dialog',
              title: t('my-collection.delete'),
              content: t('my-collection.confirm-delete'),
              promise: async () => {
                await deleteCollection(collection.ID);
              },
            });
            break;
          case 'copy':
            await copyTextToClipboard(collection.Content).catch(console.warn);
            break;
        }
      },
    });
  };

  return (
    <PageLayout
      title={t('my-collection.title')}
      className='desktop:pl-4 h-full'
      headerConfig={{
        showHeader: layout >= EmbedLayout.COMPACT,
        navigateBack: !(isDesktopModal || isDesktop),
        onNavigateBack: () => (searchParams.from === 'onboard' ? dispatch({ event: 'dismiss' }) : undefined),
        className: 'desktop:p-0',
        elementAfterNav: (
          <TabList
            className='desktop:justify-around desktop:mr-4'
            tabs={filterOptions}
            defaultActiveTab={filterType}
            onClickTab={handleChangeFilter}
          />
        ),
        actions: isDesktopModal && (
          <button
            type='button'
            className='btn btn-ghost p-0'
            onClick={() => navigate(-1)}>
            <Add className='rotate-45' />
          </button>
        ),
      }}>
      <div className='flex flex-1 w-full max-w-screen flex-col gap-4 mode-embedded:max-w-none'>
        <LoadingLayout promise={loaderData?.promises}>
          {() => (
            <Virtuoso
              className='flex-1'
              data={Object.entries(groupedCollections)}
              components={{
                EmptyPlaceholder: () => <EmptyLayout label='my-collection.empty' />,
                Footer: () => (Object.entries(groupedCollections).length > 0 ? <div className='pb-safe-or-2' /> : null),
              }}
              itemContent={(_, [key, items]) => (
                <section
                  key={key}
                  className='mx-5 desktop:mx-0'>
                  <div className='flex'>
                    <p className='py-2 text-sm'>{items?.[0]?.FromName}</p>
                    <DateDisplay
                      className='ml-4 py-2 text-sm'
                      ruleName='collection'
                      timestamp={items?.[0]?.TimeStamp}
                    />
                  </div>

                  <div
                    className='flex snap-x snap-proximity gap-4 overflow-x-auto'
                    data-items-container>
                    {items?.map((item) => {
                      const href =
                        sessionType && sessionId
                          ? (sessionType === 'session'
                              ? ROUTES.MESSAGES.SESSIONS.COLLECTIONS.BY_ID
                              : ROUTES.MESSAGES.GROUPS.COLLECTIONS.BY_ID
                            ).$buildPath({
                              params: {
                                id: sessionId,
                                collectionId: item.ID,
                              },
                            })
                          : ROUTES.MY.COLLECTIONS.BY_ID.$buildPath({
                              params: {
                                collectionId: item.ID,
                              },
                            });

                      return (
                        <Link
                          key={item.ID}
                          className='snap-start last:snap-end'
                          to={href}>
                          <CollectionItem
                            collection={item}
                            onContextMenu={(position) => handleSummonContextMenu(item, position)}
                          />
                        </Link>
                      );
                    })}
                  </div>
                </section>
              )}
            />
          )}
        </LoadingLayout>
      </div>
    </PageLayout>
  );
}

export default function CollectionsLayout({ type: sessionType, id: sessionId }: Props) {
  const { isDesktop } = useUserPlatform();
  const isShowingCollectionDetail = !useMatch({ path: ROUTES.MY.COLLECTIONS.$path(), end: true });
  const navigate = useNavigate();
  const { skipPreview } = useTypedState(ROUTES.MY.COLLECTIONS.BY_ID.FORWARD);

  if (!isDesktop) {
    if (isShowingCollectionDetail) return <Outlet />;
    return (
      <CollectionsIndexRoute
        type={sessionType}
        id={sessionId}
      />
    );
  }
  return (
    <>
      <CollectionsIndexRoute
        type={sessionType}
        id={sessionId}
      />
      {isShowingCollectionDetail &&
        (skipPreview ? (
          <Outlet />
        ) : (
          <Modal
            contentClassName='size-full !max-w-[unset] flex rounded-lg'
            className='size-full !p-0 '
            key='collection-modal'
            onClose={() => {
              navigate(ROUTES.MY.COLLECTIONS.$path());
            }}
            clickOutsideToClose
            defaultOpen={isShowingCollectionDetail}
            fullScreen>
            <div className='flex flex-1'>
              <Outlet />
            </div>
          </Modal>
        ))}
    </>
  );
}

const filterOptions: Array<{ key: CollectionFilterReducerFilterType; label: string }> = [
  {
    key: 'all',
    label: 'my-collection.all',
  },
  {
    key: 'text',
    label: 'my-collection.text',
  },
  {
    key: 'media',
    label: 'my-collection.media',
  },
  {
    key: 'files',
    label: 'my-collection.file',
  },
  {
    key: 'audio',
    label: 'my-collection.audio',
  },
];
